import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import HistoryTwoToneIcon from "@mui/icons-material/HistoryTwoTone";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import PaymentsTwoToneIcon from "@mui/icons-material/PaymentsTwoTone";
import PersonTwoToneIcon from "@mui/icons-material/PersonTwoTone";
import PrintTwoToneIcon from "@mui/icons-material/PrintTwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import {
	alpha,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Container,
	Divider,
	Grid,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography
} from "@mui/material";
import { saveAs } from "file-saver";
import { toPng } from "html-to-image";
import moment from "moment";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import GlobalLoader from "src/components/SuspenseLoader/GlobalLoader";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { ITicketDetails, ITicketListItem } from "../type/ticketScanType";
import DeleteTicketModal from "./DeleteTicketModal";
import TicketPassCard from "./TicketPassCard";
import TicketWiseScanModal from "./TicketWiseScanModal";
import UpdateTicketModal from "./UpdateTicketModal";

const getStatusChip = (status: string) => {
	const normalized = (status || "").toLowerCase();
	if (normalized === "approved" || normalized === "paid") {
		return <Chip label={status || "Paid"} color="success" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized.includes("pending")) {
		return <Chip label={status || "Pending"} color="warning" size="small" sx={{ fontWeight: 600 }} />;
	}
	if (normalized === "rejected" || normalized === "unpaid") {
		return <Chip label={status || "Unpaid"} color="error" size="small" sx={{ fontWeight: 600 }} />;
	}
	return <Chip label={status || "Active"} color="default" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
};

const SingleTicketPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const passCardRef = useRef<HTMLDivElement>(null);

	const [copied, setCopied] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [scanModalOpen, setScanModalOpen] = useState(false);
	const [updateModalOpen, setUpdateModalOpen] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const {
		data: ticket,
		isLoading,
		refetch,
	} = useApiQuery<ITicketDetails>({
		endPoint: `/api/v1/admin/visitor/ticket/${id}`,
		queryKeys: ["SingleVisitorTicket", id],
		enabled: Boolean(id),
	});

	const handlePrint = useReactToPrint({
		content: () => passCardRef.current,
		documentTitle: `Ticket-${ticket?.ticket_no || id}`,
		pageStyle: `
			@page {
				size: auto;
				margin: 0mm;
			}
			@media print {
				html, body {
					margin: 0 !important;
					padding: 0 !important;
					-webkit-print-color-adjust: exact !important;
					print-color-adjust: exact !important;
				}
				body {
					display: flex !important;
					justify-content: center !important;
					align-items: center !important;
					min-height: 100vh !important;
					background: #ffffff !important;
					padding: 15mm 0 !important;
				}
				.ticket-pass-card-container,
				.visitor-ticket-pass-card,
				.visitor-ticket-pass-car {
					width: 100% !important;
					margin: 0 auto !important;
					box-shadow: none !important;
					page-break-inside: avoid !important;
					break-inside: avoid !important;
				}
			}
		`,
	});

	const handleCopy = (text: string) => {
		navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	const handleDownload = async () => {
		if (!passCardRef.current || !ticket) return;
		try {
			setIsDownloading(true);
			const dataUrl = await toPng(passCardRef.current, {
				quality: 1,
				pixelRatio: 3,
				backgroundColor: "#ffffff",
				cacheBust: true,
				includeQueryParams: true,
			});
			saveAs(dataUrl, `BTTF-Pass-${ticket.ticket_no}.png`);
		} catch (error) {
			console.error("Failed to download ticket pass:", error);
		} finally {
			setIsDownloading(false);
		}
	};


	if (isLoading && !ticket) {
		return <GlobalLoader />;
	}

	const scans = ticket?.scans || [];
	const inScans = scans.filter((s) => (s.scan_type || "").toUpperCase() === "IN");
	const outScans = scans.filter((s) => (s.scan_type || "").toUpperCase() === "OUT");
	const lastScan = scans.length > 0 ? scans[0] : null;

	const ticketAsListItem: ITicketListItem | null = ticket
		? {
			id: ticket.id,
			fair_id: ticket.fair_id,
			visitor_id: ticket.visitor_id,
			ticket_no: ticket.ticket_no,
			ticket_config_id: ticket.ticket_config_id,
			date: ticket.date,
			quantity: ticket.quantity,
			item_amount: ticket.item_amount,
			total_amount: ticket.total_amount,
			discount: ticket.discount,
			payment_status: ticket.payment_status,
			payment_method: ticket.payment_method,
			payment_details: ticket.payment_details,
			created_by: ticket.created_by,
			created_at: ticket.created_at,
			visitor_no: ticket.visitor_no,
			visitor_name: ticket.visitor_name,
			visitor_email: ticket.visitor_email,
			visitor_phone: ticket.visitor_phone,
			scans: ticket.scans,
		}
		: null;

	return (
		<>
			<Helmet>
				<title>
					{ticket ? `BTTF - Ticket ${ticket.ticket_no}` : "BTTF - Ticket Details"}
				</title>
			</Helmet>

			<Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
				{/* Top Action Bar */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2.5,
						flexWrap: "wrap",
						gap: 1.5,
					}}
				>
					<Button
						variant="outlined"
						size="small"
						startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
						onClick={() => navigate("/visitor/ticket-list")}
						sx={{
							borderColor: "grey.400",
							color: "text.primary",
							"&:hover": { backgroundColor: "grey.100", borderColor: "grey.500" },
							borderRadius: 1.5,
							textTransform: "none",
							fontWeight: 600,
							fontSize: "0.8125rem",
							py: 0.5,
							px: 1.2,
						}}
					>
						Back to Ticket List
					</Button>

					{ticket && (
						<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", }}>
							<Button
								variant="outlined"
								color="inherit"
								size="small"
								startIcon={<PrintTwoToneIcon sx={{ fontSize: 16 }} />}
								onClick={() => handlePrint()}
								sx={{
									borderRadius: 1.5,
									textTransform: "none",
									fontWeight: 600,
									fontSize: "0.8125rem",
									py: 0.5,

									borderColor: "grey.300",
									bgcolor: "background.paper",
									"&:hover": { bgcolor: "grey.50" },
								}}
							>
								Print Pass
							</Button>

							<Button
								variant="outlined"
								color="primary"
								size="small"
								startIcon={<DownloadTwoToneIcon sx={{ fontSize: 16 }} />}
								onClick={handleDownload}
								disabled={isDownloading}
								sx={{
									borderRadius: 1.5,
									textTransform: "none",
									fontWeight: 600,
									fontSize: "0.8125rem",
									py: 0.5,

								}}
							>
								{isDownloading ? "Downloading..." : "Download Pass PNG"}
							</Button>

							<Button
								variant="contained"
								color="primary"
								size="small"
								startIcon={<QrCodeScannerTwoToneIcon sx={{ fontSize: 16 }} />}
								onClick={() => setScanModalOpen(true)}
								sx={{
									borderRadius: 1.5,
									textTransform: "none",
									fontWeight: 600,
									fontSize: "0.8125rem",
									py: 0.5,
									px: 1.4,
									boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
								}}
							>
								Scan Ticket
							</Button>

							<Button
								variant="outlined"
								color="primary"
								size="small"
								startIcon={<EditTwoToneIcon sx={{ fontSize: 16 }} />}
								onClick={() => setUpdateModalOpen(true)}
								sx={{
									borderRadius: 1.5,
									textTransform: "none",
									fontWeight: 600,
									fontSize: "0.8125rem",
									py: 0.5,
									px: 1.2,
								}}
							>
								Edit Ticket
							</Button>

							<Button
								variant="outlined"
								color="error"
								size="small"
								startIcon={<DeleteTwoToneIcon sx={{ fontSize: 16 }} />}
								onClick={() => setDeleteModalOpen(true)}
								sx={{
									borderRadius: 1.5,
									textTransform: "none",
									fontWeight: 600,
									fontSize: "0.8125rem",
									py: 0.5,
									px: 1.2,
								}}
							>
								Delete
							</Button>
						</Box>
					)}
				</Box>

				{!ticket ? (
					<Card sx={{ p: 5, textAlign: "center", borderRadius: 2 }}>
						<ConfirmationNumberTwoToneIcon
							sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
						/>
						<Typography variant="h4" color="text.secondary" gutterBottom>
							Ticket Not Found
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							The requested ticket could not be found or has been removed.
						</Typography>
						<Button
							variant="contained"
							onClick={() => navigate("/visitor/ticket-list")}
							sx={{ borderRadius: 2 }}
						>
							Go to Ticket List
						</Button>
					</Card>
				) : (
					<Grid container spacing={3}>
						{/* Top Highlight Banner */}
						<Grid item xs={12}>
							<Card
								elevation={2}
								sx={{
									borderRadius: 2.5,
									p: { xs: 1.5, md: 2 },
									background: (theme) =>
										`linear-gradient(135deg, ${alpha(
											theme.palette.primary.main,
											0.08
										)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
									border: "1px solid",
									borderColor: "divider",
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: { xs: "column", md: "row" },
										alignItems: { xs: "flex-start", md: "center" },
										justifyContent: "space-between",
										gap: 2.5,
									}}
								>
									{/* Ticket Header & Badges */}
									<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
										<Avatar
											sx={{
												width: 56,
												height: 56,
												bgcolor: "primary.main",
												color: "primary.contrastText",
												boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
											}}
										>
											<ConfirmationNumberTwoToneIcon sx={{ fontSize: 32 }} />
										</Avatar>
										<Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													flexWrap: "wrap",
												}}
											>
												<Typography variant="h3" fontWeight={700}>
													{ticket.ticket_no}
												</Typography>
												<Tooltip title={copied ? "Copied!" : "Copy Ticket No"} arrow>
													<IconButton
														size="small"
														onClick={() => handleCopy(ticket.ticket_no)}
														sx={{ p: 0.5 }}
													>
														<ContentCopyIcon sx={{ fontSize: 16 }} />
													</IconButton>
												</Tooltip>
												{getStatusChip(ticket.payment_status)}
												{ticket.date && (
													<Chip
														label={`📅 Visit: ${ticket.date}`}
														size="small"
														color="primary"
														sx={{ fontWeight: 600 }}
													/>
												)}
												{ticket.payment_method && (
													<Chip
														label={ticket.payment_method}
														size="small"
														variant="outlined"
														sx={{
															textTransform: "capitalize",
															fontWeight: 600,
														}}
													/>
												)}
											</Box>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mt: 0.4 }}
											>
												Created on {moment(ticket.created_at).format("LLL")} (
												{moment(ticket.created_at).fromNow()})
											</Typography>
										</Box>
									</Box>

									{/* Total Amount & Qty Box */}
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 2,
											bgcolor: "background.paper",
											px: 2.5,
											py: 1.5,
											borderRadius: 2,
											border: "1px solid",
											borderColor: "divider",
											boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
										}}
									>
										<Box textAlign="right">
											<Typography
												variant="caption"
												color="text.secondary"
												fontWeight={600}
											>
												TOTAL AMOUNT
											</Typography>
											<Typography
												variant="h3"
												fontWeight={800}
												color="primary.main"
											>
												৳{ticket.total_amount}
											</Typography>
										</Box>
										<Divider orientation="vertical" flexItem />
										<Box textAlign="center">
											<Typography
												variant="caption"
												color="text.secondary"
												fontWeight={600}
											>
												QTY
											</Typography>
											<Typography variant="h4" fontWeight={700}>
												{ticket.quantity || 1}
											</Typography>
										</Box>
									</Box>
								</Box>
							</Card>
						</Grid>
						{/* 1. Ticket QR Pass Card */}
						<Grid item xs={12} md={5} lg={4}>
							<Card elevation={2} sx={{ borderRadius: 2, height: "100%" }}>
								<CardHeader
									title={
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<QrCodeScannerTwoToneIcon color="primary" />
											<Typography variant="h6" fontWeight={700}>
												Ticket QR Pass
											</Typography>
										</Box>
									}
									action={
										<Tooltip title="Download Ticket Pass PNG">
											<IconButton
												color="primary"
												onClick={handleDownload}
												disabled={isDownloading}
												size="small"
											>
												<DownloadTwoToneIcon />
											</IconButton>
										</Tooltip>
									}
								/>
								<Divider />
								<CardContent
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										p: { xs: 2, sm: 2.5 },
										flexGrow: 1,
									}}
								>
									{/* Ticket Pass with background image */}
									<Box sx={{ width: "100%", maxWidth: 330, mx: "auto", mb: 2 }}>
										<TicketPassCard ref={passCardRef} ticket={ticket} />
									</Box>

									{/* Action buttons */}
									<Box
										sx={{
											display: "flex",
											gap: 1,
											width: "100%",
											maxWidth: 330,
											mx: "auto",
											mt: "auto",
										}}
									>
										<Button
											fullWidth
											size="small"
											variant="contained"
											color="primary"
											startIcon={<QrCodeScannerTwoToneIcon sx={{ fontSize: 15 }} />}
											onClick={() => setScanModalOpen(true)}
											sx={{
												borderRadius: 1.5,
												textTransform: "none",
												fontWeight: 700,
												fontSize: "0.8rem",
												py: 0.5,
											}}
										>
											Scan Now
										</Button>
										<Button
											fullWidth
											size="small"
											variant="outlined"
											color="inherit"
											startIcon={<PrintTwoToneIcon sx={{ fontSize: 15 }} />}
											onClick={() => handlePrint()}
											sx={{
												borderRadius: 1.5,
												textTransform: "none",
												fontWeight: 600,
												fontSize: "0.8rem",
												py: 0.5,
												borderColor: "grey.300",
												bgcolor: "background.paper",
												"&:hover": { bgcolor: "grey.50" },
											}}
										>
											Print Pass
										</Button>
									</Box>

									{/* Payment Note if present */}
									{ticket.payment_details?.note && (
										<Box
											sx={{
												mt: 2,
												p: 1.5,
												bgcolor: "grey.50",
												border: "1px solid",
												borderColor: "grey.200",
												borderRadius: 1.5,
												width: "100%",
											}}
										>
											<Typography variant="caption" color="text.secondary" fontWeight={600}>
												Payment Note:
											</Typography>
											<Typography variant="body2" color="text.primary" sx={{ fontStyle: "italic" }}>
												"{ticket.payment_details.note}"
											</Typography>
										</Box>
									)}
								</CardContent>
							</Card>
						</Grid>

						{/* 2. Top Summary Cards (Visitor Info, Pricing, Scan Stats in 2-column layout) */}
						<Grid item xs={12} md={7} lg={8}>
							<Grid container spacing={2.5} sx={{}}>
								{/* Visitor Info Card */}
								<Grid item xs={12} sm={6}>
									<Card
										elevation={1}
										sx={{
											height: "100%",
											borderRadius: 2,
											p: 2.2,
											display: "flex",
											flexDirection: "column",
											justifyContent: "space-between",
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
												<Avatar
													sx={{
														bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
														color: "info.main",
														width: 38,
														height: 38,
													}}
												>
													<PersonTwoToneIcon sx={{ fontSize: 20 }} />
												</Avatar>
												<Typography variant="h6" fontWeight={700}>
													Visitor Info
												</Typography>
											</Box>
											{ticket.visitor_id && (
												<Typography
													component={RouterLink}
													to={`/visitor/details/${ticket.visitor_id}`}
													variant="caption"
													color="primary.main"
													sx={{ textDecoration: "none", fontWeight: 600 }}
												>
													View Profile →
												</Typography>
											)}
										</Box>
										<Divider sx={{ my: 1 }} />
										<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
											<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
												<Typography variant="caption" color="text.secondary">
													Name:
												</Typography>
												<Typography variant="body2" fontWeight={700}>
													{ticket.visitor_name || "N/A"}
												</Typography>
											</Box>
											{ticket.visitor_no && (
												<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<Typography variant="caption" color="text.secondary">
														Visitor No:
													</Typography>
													<Chip
														label={ticket.visitor_no}
														size="small"
														variant="outlined"
														sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
													/>
												</Box>
											)}
											{ticket.visitor_phone && (
												<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<Typography variant="caption" color="text.secondary">
														Phone:
													</Typography>
													<Typography variant="body2" fontWeight={600}>
														{ticket.visitor_phone}
													</Typography>
												</Box>
											)}
											{ticket.visitor_email && (
												<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<Typography variant="caption" color="text.secondary">
														Email:
													</Typography>
													<Typography
														variant="body2"
														fontWeight={500}
														sx={{
															maxWidth: 180,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
														title={ticket.visitor_email}
													>
														{ticket.visitor_email}
													</Typography>
												</Box>
											)}
											{ticket.date && (
												<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
													<Typography variant="caption" color="text.secondary">
														Visiting Date:
													</Typography>
													<Typography
														variant="body2"
														fontWeight={500}
														sx={{
															maxWidth: 180,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
														title={ticket.date}
													>

														{moment(ticket.date).format("DD-MMM-YYYY")}

													</Typography>
												</Box>
											)}
										</Box>
									</Card>
								</Grid>

								{/* Pricing & Payment Breakdown */}
								<Grid item xs={12} sm={6}>
									<Card
										elevation={1}
										sx={{
											height: "100%",
											borderRadius: 2,
											p: 2.2,
											display: "flex",
											flexDirection: "column",
											justifyContent: "space-between",
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}>
											<Avatar
												sx={{
													bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
													color: "success.main",
													width: 38,
													height: 38,
												}}
											>
												<PaymentsTwoToneIcon sx={{ fontSize: 20 }} />
											</Avatar>
											<Typography variant="h6" fontWeight={700}>
												Pricing & Payment
											</Typography>
										</Box>
										<Divider sx={{ my: 1 }} />
										<Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
											<Box sx={{ display: "flex", justifyContent: "space-between" }}>
												<Typography variant="caption" color="text.secondary">
													Unit Price:
												</Typography>
												<Typography variant="body2" fontWeight={600}>
													৳{ticket.item_amount || "0.00"}
												</Typography>
											</Box>
											<Box sx={{ display: "flex", justifyContent: "space-between" }}>
												<Typography variant="caption" color="text.secondary">
													Quantity:
												</Typography>
												<Typography variant="body2" fontWeight={600}>
													{ticket.quantity || 1} pcs
												</Typography>
											</Box>
											{Number(ticket.discount || 0) > 0 && (
												<Box sx={{ display: "flex", justifyContent: "space-between" }}>
													<Typography variant="caption" color="text.secondary">
														Discount:
													</Typography>
													<Typography variant="body2" color="error.main" fontWeight={600}>
														-৳{ticket.discount}
													</Typography>
												</Box>
											)}
											{Number(ticket.extra_charge || 0) > 0 && (
												<Box sx={{ display: "flex", justifyContent: "space-between" }}>
													<Typography variant="caption" color="text.secondary">
														Extra Charge:
													</Typography>
													<Typography variant="body2" fontWeight={600}>
														+৳{ticket.extra_charge}
													</Typography>
												</Box>
											)}
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													pt: 0.5,
													borderTop: "1px dashed",
													borderColor: "divider",
												}}
											>
												<Typography variant="body2" fontWeight={700}>
													Total:
												</Typography>
												<Typography variant="body2" fontWeight={800} color="primary.main">
													৳{ticket.total_amount}
												</Typography>
											</Box>
										</Box>
									</Card>
								</Grid>

								{/* Scan Stats (Wide Horizontal Layout) */}
								<Grid item xs={12}>
									<Card
										elevation={1}
										sx={{
											borderRadius: 2,
											p: 2.2,
										}}
									>
										<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
												<Avatar
													sx={{
														bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
														color: "warning.main",
														width: 38,
														height: 38,
													}}
												>
													<HistoryTwoToneIcon sx={{ fontSize: 20 }} />
												</Avatar>
												<Typography variant="h6" fontWeight={700}>
													Scan Stats
												</Typography>
											</Box>
											<Chip
												label={`${scans.length} Total Scans`}
												size="small"
												color={scans.length > 0 ? "primary" : "default"}
												sx={{ fontWeight: 700 }}
											/>
										</Box>
										<Divider sx={{ mb: 1.5 }} />
										<Grid container spacing={1.5}>
											<Grid item xs={12} sm={4}>
												<Box
													sx={{
														p: 1.5,
														bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
														borderRadius: 1.5,
														border: "1px solid",
														borderColor: (theme) => alpha(theme.palette.success.main, 0.2),
													}}
												>
													<Typography variant="caption" color="text.secondary" fontWeight={600}>
														ENTRY (IN)
													</Typography>
													<Typography variant="h4" fontWeight={800} color="success.main" sx={{ mt: 0.2 }}>
														{inScans.length}
													</Typography>
												</Box>
											</Grid>
											<Grid item xs={12} sm={4}>
												<Box
													sx={{
														p: 1.5,
														bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
														borderRadius: 1.5,
														border: "1px solid",
														borderColor: (theme) => alpha(theme.palette.warning.main, 0.2),
													}}
												>
													<Typography variant="caption" color="text.secondary" fontWeight={600}>
														EXIT (OUT)
													</Typography>
													<Typography variant="h4" fontWeight={800} color="warning.main" sx={{ mt: 0.2 }}>
														{outScans.length}
													</Typography>
												</Box>
											</Grid>
											<Grid item xs={12} sm={4}>
												<Box
													sx={{
														p: 1.5,
														bgcolor: "grey.50",
														borderRadius: 1.5,
														border: "1px solid",
														borderColor: "grey.200",
														height: "100%",
														display: "flex",
														flexDirection: "column",
														justifyContent: "center",
													}}
												>
													<Typography variant="caption" color="text.secondary" fontWeight={600}>
														LAST SCAN ACTIVITY
													</Typography>
													<Typography
														variant="body2"
														fontWeight={700}
														color="text.primary"
														sx={{ mt: 0.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
													>
														{lastScan
															? `${lastScan.scan_type} (${moment(lastScan.scan_time).format(
																"hh:mm A"
															)})`
															: "Never"}
													</Typography>
												</Box>
											</Grid>
										</Grid>
									</Card>
								</Grid>
							</Grid>
						</Grid>

						{/* Scan History Table Card */}
						<Grid item xs={12}>
							<Card elevation={2} sx={{ borderRadius: 2 }}>
								<CardHeader
									title={
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<HistoryTwoToneIcon color="primary" />
											<Typography variant="h6" fontWeight={700}>
												Scan History Logs ({scans.length})
											</Typography>
										</Box>
									}
									subheader="Chronological entry and exit records for this ticket"
								/>
								<Divider />
								<TableContainer sx={{ maxHeight: 520 }}>
									<Table stickyHeader size="small">
										<TableHead>
											<TableRow sx={{ bgcolor: "#f8fafc" }}>
												<TableCell sx={{ fontWeight: "bold", width: 50 }}>SL</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>Scan Time</TableCell>
												<TableCell sx={{ fontWeight: "bold" }} align="center">
													Type
												</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>Scanned By</TableCell>
												<TableCell sx={{ fontWeight: "bold" }}>Visitor / Org</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{scans.length > 0 ? (
												scans.map((scan, idx) => (
													<TableRow hover key={scan.id || idx}>
														<TableCell>{idx + 1}</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight={600}>
																{moment(scan.scan_time).format("DD MMM YYYY, hh:mm:ss A")}
															</Typography>
															<Typography variant="caption" color="text.secondary">
																{moment(scan.scan_time).fromNow()}
															</Typography>
														</TableCell>
														<TableCell align="center">
															{scan.scan_type?.toUpperCase() === "IN" ? (
																<Chip
																	icon={<LoginIcon sx={{ fontSize: "14px !important" }} />}
																	label="IN"
																	color="success"
																	size="small"
																	sx={{ fontWeight: 700, minWidth: 65 }}
																/>
															) : (
																<Chip
																	icon={<LogoutIcon sx={{ fontSize: "14px !important" }} />}
																	label="OUT"
																	color="warning"
																	size="small"
																	sx={{ fontWeight: 700, minWidth: 65 }}
																/>
															)}
														</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight={600}>
																{scan.scan_by_name || "Staff"}
															</Typography>
															{scan.scan_by_email && (
																<Typography variant="caption" color="text.secondary">
																	{scan.scan_by_email}
																</Typography>
															)}
														</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight={600}>
																{scan.visitor_name || ticket.visitor_name || "-"}
															</Typography>
															{(scan.visitor_organization || scan.visitor_designation) && (
																<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
																	{[scan.visitor_designation, scan.visitor_organization]
																		.filter(Boolean)
																		.join(", ")}
																</Typography>
															)}
														</TableCell>
													</TableRow>
												))
											) : (
												<TableRow>
													<TableCell colSpan={5} align="center" sx={{ py: 6 }}>
														<QrCodeScannerTwoToneIcon
															sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5, mb: 1 }}
														/>
														<Typography variant="body1" color="text.secondary" fontWeight={600}>
															No scan records yet
														</Typography>
														<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
															This ticket has not been scanned at the gate yet.
														</Typography>
														<Button
															size="small"
															variant="contained"
															color="primary"
															startIcon={<QrCodeScannerTwoToneIcon />}
															onClick={() => setScanModalOpen(true)}
															sx={{ borderRadius: 1.5, textTransform: "none", mt: 1 }}
														>
															Record Scan Now
														</Button>
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Card>
						</Grid>
					</Grid>
				)}
			</Container>

			{/* Manual Scan Modal */}
			<TicketWiseScanModal
				open={scanModalOpen}
				ticket={ticketAsListItem}
				handleClose={() => {
					setScanModalOpen(false);
					refetch();
				}}
			/>

			{/* Update Ticket Modal */}
			{updateModalOpen && ticketAsListItem && (
				<UpdateTicketModal
					open={updateModalOpen}
					ticket={ticketAsListItem}
					handleClose={() => {
						setUpdateModalOpen(false);
						refetch();
					}}
				/>
			)}

			{/* Delete Ticket Modal */}
			{deleteModalOpen && ticketAsListItem && (
				<DeleteTicketModal
					open={deleteModalOpen}
					ticket={ticketAsListItem}
					handleClose={() => {
						setDeleteModalOpen(false);
						navigate("/visitor/ticket-list");
					}}
				/>
			)}
		</>
	);
};

export default SingleTicketPage;
